import { useState } from 'react';

import styled from '@emotion/styled';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import { browserSupportsWebAuthn, passkeyErrorMessage, UNSUPPORTED } from '../../../hooks/auth/passkeys/webauthn';
import usePasskeyApi from '../../../hooks/auth/passkeys/usePasskeyApi';
import useSetAuthentication from '../../../hooks/auth/useSetAuthentication';
import { ILoginTokenResponse } from '../../../models/auth/signIn.model';
import RecoveryCodes from '../Passkeys/RecoveryCodes';

interface Props {
  ticket: string;
  onBack: () => void;
}

// Step two of signing in: the passkey (ops spec D15). The ticket lives only
// here, in component state. "Use a recovery code" gives a token that can only
// add a passkey (D19); it's held in memory and never becomes the session.
const PasskeyStep: React.FC<Props> = ({ ticket, onBack }) => {
  const api = usePasskeyApi();
  const { setAuthentication } = useSetAuthentication();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(browserSupportsWebAuthn() ? null : UNSUPPORTED);
  const [mode, setMode] = useState<'passkey' | 'recovery' | 'add' | 'codes'>('passkey');
  const [code, setCode] = useState('');
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [session, setSession] = useState<ILoginTokenResponse | null>(null);

  const signIn = (tokens: ILoginTokenResponse) =>
    setAuthentication({
      status: 'success',
      message: 'Signed in',
      data: { user: null, accessToken: { token: tokens.access_token }, refreshToken: { token: tokens.refresh_token } },
    });

  const signInWithPasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      const optionsJSON = await api<any>('api/v1/auth/admin/passkey/options', { ticket });
      const credential = await startAuthentication({ optionsJSON });
      signIn(await api<ILoginTokenResponse>('api/v1/auth/admin/passkey/verify', { ticket, credential }));
    } catch (err) {
      setError(passkeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { access_token } = await api<ILoginTokenResponse>('api/v1/auth/admin/recovery', { ticket, code });
      setRecoveryToken(access_token);
      setMode('add');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      const optionsJSON = await api<any>('api/v1/admin/passkeys/register/options', {}, recoveryToken!);
      const credential = await startRegistration({ optionsJSON });
      const result = await api<ILoginTokenResponse & { recovery_codes: string[] }>(
        'api/v1/admin/passkeys/register/verify',
        { credential, name: deviceName() },
        recoveryToken!
      );
      setRecoveryToken(null);
      setSession(result);
      setNewCodes(result.recovery_codes);
      setMode('codes');
    } catch (err) {
      setError(passkeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'codes' && session) {
    return <RecoveryCodes codes={newCodes} onDone={() => signIn(session)} doneLabel="I've saved them, continue" />;
  }

  return (
    <Box>
      {mode === 'passkey' && (
        <>
          <p>Use your passkey to finish signing in: your fingerprint, face or device PIN.</p>
          <PrimaryBlackButton type='button' className='large' disabled={busy || !browserSupportsWebAuthn()} onClick={signInWithPasskey}>
            {busy ? 'Waiting for your passkey…' : 'Use your passkey'}
          </PrimaryBlackButton>
          <Links>
            <button type='button' onClick={() => setMode('recovery')}>
              Use a recovery code
            </button>
            <button type='button' onClick={onBack}>
              Back
            </button>
          </Links>
        </>
      )}
      {mode === 'recovery' && (
        <form onSubmit={submitCode}>
          <p>Lost your passkey? Enter one of your recovery codes. You’ll add a new passkey next.</p>
          <input
            autoComplete='one-time-code'
            placeholder='XXXXX-XXXXX'
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <PrimaryBlackButton type='submit' className='large' disabled={busy || code.length < 10}>
            {busy ? 'Checking…' : 'Use code'}
          </PrimaryBlackButton>
          <Links>
            <button type='button' onClick={() => setMode('passkey')}>
              Use my passkey instead
            </button>
          </Links>
        </form>
      )}
      {mode === 'add' && (
        <>
          <p>Code accepted. Add a passkey on this device now: you’ll use it to sign in from now on.</p>
          <PrimaryBlackButton type='button' className='large' disabled={busy || !browserSupportsWebAuthn()} onClick={addPasskey}>
            {busy ? 'Waiting for your passkey…' : 'Add a passkey'}
          </PrimaryBlackButton>
        </>
      )}
      {error && <ErrorText role='alert'>{error}</ErrorText>}
    </Box>
  );
};

export default PasskeyStep;

export const deviceName = (): string => {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Passkey';
};

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  input {
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    border-radius: 12px;
    padding: 12px 14px;
    font: inherit;
    font-size: 16px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;

  button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-size: 14px;
    color: ${({ theme }) => theme.palette.greenColor};
    cursor: pointer;
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 14px;
`;
